/**
 * Commander Mantine CSS variables resolver
 *
 * Provides a clean, scheme-aware override path for Mantine 9 CSS variables
 * that need to be re-pointed at our own design tokens. Wired into both
 * `<MantineProvider>` instances (admin + kiosk).
 *
 * Replaces the earlier P6.4 stop-gap which used
 * `:root[data-mantine-color-scheme=*] { --mantine-color-dimmed: ... !important; }`
 * in `apps/admin/index.html`. The resolver is the documented Mantine 9
 * pattern; Mantine emits the resolver's `variables` / `light` / `dark`
 * blocks inside its own runtime <style> block so we no longer need to fight
 * source-order with `!important`.
 *
 * Why re-point `--mantine-color-dimmed`:
 *   - Mantine's default dimmed colour (gray.6 light / dark.3 dark) fails
 *     WCAG AA 4.5:1 on a `--bg-card` surface in the light scheme.
 *   - Our own `--text-muted` token is slate-500 (light) / slate-400 (dark),
 *     both of which clear 4.5:1. Pointing dimmed at `--text-muted` reuses
 *     the same value engineers already use directly via `var(--text-muted)`
 *     and keeps Mantine's built-in `c="dimmed"` consumers in compliance.
 *
 * If you need to override another Mantine variable in future, add it here
 * (in `light` and `dark` if it varies by scheme, or `variables` if not) —
 * do NOT reintroduce `!important` blocks in index.html.
 */

import type { CSSVariablesResolver } from '@mantine/core';

export const commanderResolver: CSSVariablesResolver = () => ({
  variables: {
    // Scheme-agnostic. Empty for now — every override below varies by scheme.
  },
  light: {
    '--mantine-color-dimmed': 'var(--text-muted)',
  },
  dark: {
    // The token itself resolves to a dark-scheme value via tokens.css /
    // index.html when `html.dark` is set, so pointing both schemes at
    // `var(--text-muted)` is correct.
    '--mantine-color-dimmed': 'var(--text-muted)',
  },
});
